// Maps the prototype's saved records (camelCase, epoch-ms stamps) to database rows.
// Used by scripts/import-seed.ts and by the app's local demo mode.
import type { Crane, Drawings, Job, Stage, Status, Task, Tracking, Waiting } from './types';
import { melbourneToUtcIso } from './dates';
import { normaliseQuote, quoteType } from './quote';
import { CRANE, DRAWINGS, STATUSES } from './rules';

type Raw = Record<string, unknown>;

/** Seed files hold either plain records or the artifact export shape {id, data: {...}}. */
export function unwrap(list: unknown[]): Raw[] {
  return list.map((x) => {
    const r = x as Raw;
    return r && typeof r === 'object' && r.data && typeof r.data === 'object' ? { ...(r.data as Raw), id: r.id } : r;
  });
}

const STATUS_KEYS = STATUSES.map(([k]) => k) as string[];
const WAITING = ['PM', 'Builder', 'Detailers'];

const str = (v: unknown): string | null => (v === undefined || v === null || v === '' ? null : String(v));
const isoDate = (v: unknown): string | null => {
  const s = str(v);
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  throw new Error(`not an ISO date: ${s}`);
};
/** Epoch ms, or an ISO string, → ISO timestamp. */
const stamp = (v: unknown): string | null => {
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'number') return new Date(v).toISOString();
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) throw new Error(`not a timestamp: ${v}`);
  return d.toISOString();
};
const num = (v: unknown): number => {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n)) throw new Error(`not a number: ${v}`);
  return Math.round(n * 100) / 100;
};
function oneOf<T extends string>(v: unknown, allowed: readonly string[], fallback: T | null, field: string): T | null {
  const s = str(v);
  if (s === null) return fallback;
  if (!allowed.includes(s)) throw new Error(`${field} "${s}" is not one of ${allowed.join(', ')}`);
  return s as T;
}
const days = (v: unknown): number | null => {
  if (v === undefined || v === null || v === '') return null;
  const n = Math.round(Number(v));
  if (!Number.isFinite(n) || n < 0) throw new Error(`daysOnsite "${v}" is not a whole number ≥ 0`);
  return n;
};

function tracking(r: Raw): Tracking {
  return {
    status: oneOf<Status>(r.status, STATUS_KEYS, 'not-started', 'status')!,
    prev_status: oneOf<Status>(r.prevStatus, STATUS_KEYS.filter((s) => s !== 'hold'), null, 'prevStatus'),
    hold_since: isoDate(r.holdSince),
    drawings: oneOf<Drawings>(r.drawings, DRAWINGS, 'Not started', 'drawings')!,
    draw_waiting: oneOf<Waiting>(r.drawWaiting, WAITING, null, 'drawWaiting'),
    drawings_updated: isoDate(r.drawingsUpdated),
    fab_date: isoDate(r.fabDate),
    install_date: isoDate(r.installDate),
    crane: oneOf<Crane>(r.crane, CRANE, 'Not booked', 'crane')!,
    days_onsite: days(r.daysOnsite),
    completed_date: isoDate(r.completedDate),
    notes: str(r.notes),
    updated_at: stamp(r.updatedAt),
    updated_by: null,
  };
}

/** The prototype stored the quote date as YYYY-MM-DD; read it as midnight Melbourne. */
function quoteDate(v: unknown): string | null {
  const d = isoDate(v);
  return d ? melbourneToUtcIso(+d.slice(0, 4), +d.slice(5, 7), +d.slice(8, 10)) : null;
}

export function mapJob(r: Raw): Job {
  const quote = normaliseQuote(str(r.quote) ?? str(r.id) ?? '');
  if (!quote) throw new Error('job has no quote number');
  return {
    quote,
    pm: str(r.pm) ?? '',
    type: (str(r.type) as Job['type']) ?? quoteType(quote),
    quote_date: quoteDate(r.date),
    address: str(r.address) ?? '',
    builder: str(r.builder) ?? '',
    quoted: num(r.quoted), invoiced: num(r.invoiced), remaining: num(r.remaining),
    sheet_completed: false, sheet_completed_date: null,
    last_synced_at: stamp(r.syncedAt),
    handed_date: isoDate(r.handedDate),
    staged: r.staged === true,
    ...tracking(r),
  };
}

export function mapStage(r: Raw): Stage {
  if (!str(r.id)) throw new Error('stage has no id');
  if (!str(r.parent)) throw new Error(`stage ${r.id} has no parent`);
  return {
    id: String(r.id),
    parent_quote: normaliseQuote(String(r.parent)),
    name: str(r.name) ?? '',
    sort_order: Math.round(Number(r.order ?? 0)) || 0,
    created_at: stamp(r.createdAt),
    ...tracking(r),
  };
}

export function mapTask(r: Raw): Task {
  if (!str(r.id)) throw new Error('task has no id');
  if (!str(r.text)) throw new Error(`task ${r.id} has no text`);
  const quote = str(r.quote);
  return {
    id: String(r.id),
    pm: str(r.pm) ?? '',
    // a task's quote may be a job quote or a stage id; only quote numbers get normalised
    quote: quote && /^q\d/i.test(quote) ? normaliseQuote(quote) : quote,
    text: String(r.text),
    due: isoDate(r.due),
    done: r.done === true,
    done_at: stamp(r.doneAt),
    created_at: stamp(r.createdAt),
    created_by: null,
  };
}
